// src/components/UserDetails.tsx
import React, { useState, useEffect } from 'react';
import { Form, Button, Col, Alert, Card, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import useFetchUserProfile from '../../hooks/useFetchAccount';
import useUpdateAccount from '../../hooks/useUpdateAccount';
import { UserProfile } from '../../interfaces';
import { Paragraph } from '../../components/common/SimpleControls/Paragraph';
import { useSession } from '../../contexts/SessionContext';
import useLogout from '../../hooks/useLogout';

const UserDetails: React.FC = () => {
  const { t } = useTranslation();
  const { profile, error: fetchError } = useFetchUserProfile();
  const { updateProfile, error: updateError } = useUpdateAccount();
  const { sessionState, dispatch } = useSession();
  const { logout, error } = useLogout();
  const [editMode, setEditMode] = useState<boolean>(false);
  const [profileData, setProfileData] = useState<UserProfile>({
    userName: '',
    token: '',
    name: '',
    email: '',
    doliKey: '',
    gitToken: '',
    firstName: '',
    lastName: ''
  });

  useEffect(() => {
    if (profile) {
      setProfileData(profile);
    }
  }, [profile]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setProfileData(prevState => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const success = await updateProfile(profileData);
    if (success) {
      setEditMode(false);
    }
  };

  const handleLogout = async () => {
    const success = await logout(sessionState.account);
    if (success) {
      dispatch({ type: 'LOGOUT' });
    } else if (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <Card className="bg-transparent border-0">
      <Card.Header className='border-0 bg-transparent'>
        <Card.Title className="text-secondary text-center fs-1 fw-bold m-3">{t('userProfile.title')}</Card.Title>
      </Card.Header>
      <Card.Body>
        <Form onSubmit={handleSubmit}>
          {fetchError && <Alert variant="danger">{fetchError}</Alert>}
          {updateError && <Alert variant="danger">{updateError}</Alert>}

          <Row className="mb-4 d-flex justify-content-center">
            <Col md={6}>
              <Form.Group>
                <Form.Label className="mb-0 fs-4 fw-medium text-secondary">{t('userProfile.userName')}</Form.Label>
                <Form.Control
                  type="text"
                  name="userName"
                  value={profileData.userName}
                  onChange={handleChange}
                  disabled={!editMode}
                  className='form__field text-secondary fw-medium fs-5 bg-white px-1 py-2'
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="mb-0 fs-4 fw-medium text-secondary">{t('email')}</Form.Label>
                <Form.Control
                  type="email"
                  name="email"
                  value={profileData.email}
                  onChange={handleChange}
                  disabled={!editMode}
                  className='form__field text-secondary fw-medium fs-5 bg-white px-1 py-2'
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-4">
            <Col md={6}>
              <Form.Group>
                <Form.Label className="mb-0 fs-4 fw-medium text-secondary">{t('userProfile.firstName')}</Form.Label>
                <Form.Control
                  type="text"
                  name="firstName"
                  value={profileData.firstName}
                  onChange={handleChange}
                  disabled={!editMode}
                  className='form__field text-secondary fw-medium fs-5 bg-white px-1 py-2 text-capitalize'
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="mb-0 fs-4 fw-medium text-secondary">{t('userProfile.lastName')}</Form.Label>
                <Form.Control
                  type="text"
                  name="lastName"
                  value={profileData.lastName}
                  onChange={handleChange}
                  disabled={!editMode}
                  className='form__field text-secondary fw-medium fs-5 bg-white px-1 py-2 text-uppercase'
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-4">
            <Col md={6}>
              <Form.Group>
                <Form.Label className="mb-0 fs-4 fw-medium text-secondary">{t('userProfile.gitToken')}</Form.Label>
                <Form.Control
                  type="gitToken"
                  name="gitToken"
                  value={profileData.gitToken ?? ''}
                  onChange={handleChange}
                  disabled={!editMode}
                  className='form__field text-secondary fw-medium fs-5 bg-white px-1 py-2'
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="mb-0 fs-4 fw-medium text-secondary">{t('userProfile.doliKey')}</Form.Label>
                <Form.Control
                  type="doliKey"
                  name="doliKey"
                  value={profileData.doliKey ?? ''}
                  onChange={handleChange}
                  disabled={!editMode}
                  className='form__field text-secondary fw-medium fs-5 bg-white px-1 py-2'
                />
              </Form.Group>
            </Col>
          </Row>

          {editMode ? (
            <>
              <Col className="d-flex justify-content-center">
                <Button className="py-2 px-3 fs-5" variant="secondary" type="submit">
                  {t('userProfile.save')}
                </Button>
                <Button className="ms-3 py-2 px-4 fs-5" variant="primary" onClick={() => setEditMode(false)}>
                  {t('cancel')}
                </Button>
              </Col>
            </>
          ) : (
            <Col className="d-flex justify-content-center">
              <Button className="btn btn-secondary py-2 px-3 rounded-3 fs-5 text-white fw-medium" variant="primary" onClick={() => setEditMode(true)}>
                {t('userProfile.edit')}
              </Button>
            </Col>
          )}
        </Form>
        <Paragraph className='text-secondary text-center mt-3'>{t("logoutConfirmation")} <a className="fw-semibold clickable-row link-opacity-50-hover link-underline link-underline-opacity-75" onClick={handleLogout} href="">{t("clickHere")}</a></Paragraph>
      </Card.Body>
    </Card>
  );
};

export default UserDetails;
